/**
 * Wizard step 1: the title and the city.
 *
 * The step exists on its own because the title is the only field that gets quoted
 * everywhere else (share page, list, calendar), so it is validated here before
 * anything downstream can be filled in with a half-finished draft. The time window
 * lives only on the time and weather step: showing it in two steps made the same
 * field look like two different settings.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { CITY_KEYS, type CityKey } from '@shared/cities'
import { LIMITS } from '@shared/constants'

import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Field, FieldGroup } from '../../../components/ui/field'
import { Input, Label } from '../../../components/ui/input'
import { WizardFooter } from '../../../components/layout/wizard'
import { cityLabelKey, nowLocalDateTime } from '../../../lib/format'
import { useEventDraft } from '../../../stores/drafts'

/**
 * Next Saturday at 14:00 in the event city's wall clock, formatted like every other
 * event date.
 *
 * "Next" means the coming Saturday, or the one after when the city is already past
 * Saturday afternoon. The city's clock decides that, not the browser's: the value
 * lands in a field the organizer can change, and it has to mean the same moment for
 * everybody who reads the event.
 */
export function nextSaturdayAfternoon(now: Date = new Date()): string {
  const [datePart = '', timePart = ''] = nowLocalDateTime(now).split('T')
  const [year, month, day] = datePart.split('-').map(Number) as [number, number, number]
  const [hour, minute] = timePart.split(':').map(Number) as [number, number]

  let daysAhead = (6 - new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 7) % 7
  if (daysAhead === 0 && (hour > 14 || (hour === 14 && minute > 0))) {
    daysAhead = 7
  }

  const target = new Date(Date.UTC(year, month - 1, day + daysAhead))
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${target.getUTCFullYear()}-${pad(target.getUTCMonth() + 1)}-${pad(target.getUTCDate())}T14:00`
}

export function NewEventTitlePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { draft, update } = useEventDraft()
  const [localError, setLocalError] = useState<string | null>(null)

  // A start time is proposed once, on mount, so the weather step always has a
  // date to ask the suggestion endpoint about. Clearing the field afterwards is
  // a deliberate edit, which is why this effect does not re-seed it.
  useEffect(() => {
    if (draft.eventDateStart.length === 0) {
      update({ eventDateStart: nextSaturdayAfternoon() })
    }
  }, [draft.eventDateStart, update])

  function goNext() {
    const title = draft.title.trim()
    if (title.length < LIMITS.eventTitleMinLength || title.length > LIMITS.eventTitleMaxLength) {
      setLocalError(t('eventNew.titleError'))
      return
    }
    setLocalError(null)
    update({ title })
    navigate('/me/events/new/activities')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('eventNew.stepTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <FieldGroup>
          <Field
            label={t('eventNew.titleLabel')}
            htmlFor="draft-title"
            required
            error={localError}
            hint={t('eventNew.titleHint')}
          >
            <Input
              id="draft-title"
              name="title"
              maxLength={LIMITS.eventTitleMaxLength}
              placeholder={t('eventNew.titlePlaceholder')}
              value={draft.title}
              aria-invalid={Boolean(localError)}
              onChange={(event) => update({ title: event.target.value })}
            />
          </Field>

          <div className="flex flex-col gap-2">
            <Label htmlFor="draft-city">{t('eventNew.cityLabel')}</Label>
            <select
              id="draft-city"
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm shadow-sm"
              value={draft.city}
              onChange={(event) => update({ city: event.target.value as CityKey })}
            >
              {CITY_KEYS.map((city) => (
                <option key={city} value={city}>
                  {t(cityLabelKey(city))}
                </option>
              ))}
            </select>
          </div>
        </FieldGroup>

        <WizardFooter
          backTo="/me/events"
          backLabel={t('common.back')}
          nextLabel={t('common.next')}
          onNext={goNext}
        />
      </CardContent>
    </Card>
  )
}
